import React, { useMemo, useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {
  PIPELINE_LEAD_STATUSES,
  PIPELINE_STATUS_LABELS,
  countActiveLeadPipelineFilters,
  getDefaultLeadPipelineFilters
} from '../../services/leadsService';

const { FiFilter, FiChevronDown, FiChevronUp, FiX } = FiIcons;

const PAYMENT_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'paid', label: 'Paid' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'free', label: 'Free' }
];

const ACCOUNT_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' }
];

const selectClass =
  'w-full min-w-0 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white';

const labelClass = 'block text-xs font-medium text-gray-500 mb-1';

const LeadPipelineFilterBar = ({
  filters,
  onFiltersChange,
  onClear,
  eventOptions = [],
  agentOptions = [],
  showAgentFilter = false,
  agentsLoading = false,
  defaultExpanded = true
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const activeCount = useMemo(() => countActiveLeadPipelineFilters(filters), [filters]);

  const update = (patch) => {
    onFiltersChange({ ...filters, ...patch });
  };

  return (
    <div className="border-b border-gray-200 bg-gray-50/80">
      <div className="px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
          aria-expanded={expanded}
        >
          <SafeIcon icon={FiFilter} className="w-4 h-4 text-gray-500" />
          Filters{activeCount > 0 ? ` (${activeCount})` : ''}
          <SafeIcon icon={expanded ? FiChevronUp : FiChevronDown} className="w-4 h-4 text-gray-400" />
        </button>
        {activeCount > 0 && (
          <button
            type="button"
            onClick={onClear}
            className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            <SafeIcon icon={FiX} className="w-4 h-4" />
            Clear Filters
          </button>
        )}
      </div>

      {expanded && (
        <div className="px-4 sm:px-6 pb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          <div>
            <label className={labelClass} htmlFor="lead-filter-event">
              Event
            </label>
            <select
              id="lead-filter-event"
              value={filters.eventId}
              onChange={(e) => update({ eventId: e.target.value })}
              className={selectClass}
            >
              <option value="">All events</option>
              {eventOptions.map((ev) => (
                <option key={ev.id} value={ev.id}>
                  {ev.title}
                </option>
              ))}
            </select>
          </div>

          {showAgentFilter && (
            <div>
              <label className={labelClass} htmlFor="lead-filter-agent">
                Agent / Inviter
              </label>
              <select
                id="lead-filter-agent"
                value={filters.agentId}
                onChange={(e) => update({ agentId: e.target.value })}
                className={selectClass}
                disabled={agentsLoading}
              >
                <option value="">{agentsLoading ? 'Loading agents…' : 'All agents'}</option>
                {agentOptions.map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className={labelClass} htmlFor="lead-filter-status">
              Lead Status
            </label>
            <select
              id="lead-filter-status"
              value={filters.leadStatus}
              onChange={(e) => update({ leadStatus: e.target.value })}
              className={selectClass}
            >
              <option value="">All statuses</option>
              {PIPELINE_LEAD_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {PIPELINE_STATUS_LABELS[status] ?? status}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelClass} htmlFor="lead-filter-payment">
              Payment Status
            </label>
            <select
              id="lead-filter-payment"
              value={filters.paymentStatus}
              onChange={(e) => update({ paymentStatus: e.target.value })}
              className={selectClass}
            >
              {PAYMENT_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelClass} htmlFor="lead-filter-date-from">
              From
            </label>
            <input
              id="lead-filter-date-from"
              type="date"
              value={filters.dateFrom}
              onChange={(e) => update({ dateFrom: e.target.value })}
              className={selectClass}
            />
          </div>

          <div>
            <label className={labelClass} htmlFor="lead-filter-date-to">
              To
            </label>
            <input
              id="lead-filter-date-to"
              type="date"
              value={filters.dateTo}
              onChange={(e) => update({ dateTo: e.target.value })}
              className={selectClass}
            />
          </div>

          <div>
            <label className={labelClass} htmlFor="lead-filter-account">
              Has Account
            </label>
            <select
              id="lead-filter-account"
              value={filters.hasAccount}
              onChange={(e) => update({ hasAccount: e.target.value })}
              className={selectClass}
            >
              {ACCOUNT_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

export { getDefaultLeadPipelineFilters };
export default LeadPipelineFilterBar;
